/**
 * Feature E04-F02-T05 — pure model for the inbox link-to-project autocomplete.
 *
 * Framework-free helpers ported from the legacy `inbox.js` showLinkEmailDialog:
 *   - only ACTIVE projects are linkable (`status === 'active'`);
 *   - the option label is `role_name (client_name)` (with graceful fallbacks);
 *   - the search haystack is role_name + client_name (case-insensitive includes).
 * Kept here so the option filtering/labelling is unit-testable without a DOM.
 */
import type { ProjectListItemDTO } from '@eer/shared';

/** Only `status === 'active'` projects can be linked (legacy parity). */
export function activeProjects(projects: readonly ProjectListItemDTO[]): ProjectListItemDTO[] {
  return projects.filter((p) => p.status === 'active');
}

/**
 * The dropdown label for a project: `role_name (client_name)`. A missing role
 * falls back to `Project #<id>`; a missing client drops the parenthetical
 * (legacy `${role} (${client})` with empty-guarding).
 */
export function projectLinkLabel(project: ProjectListItemDTO): string {
  const role = (project.role_name ?? '').trim() || `Project #${project.id}`;
  const client = (project.client_name ?? '').trim();
  return client ? `${role} (${client})` : role;
}

/** Lowercased search haystack for a project (role + client). */
export function projectSearchHaystack(project: ProjectListItemDTO): string {
  return [project.role_name, project.client_name].filter(Boolean).join(' ').toLowerCase();
}

/**
 * Filter ACTIVE projects by a free-text query over role + client (legacy
 * autocomplete). An empty query returns all active projects.
 */
export function filterLinkProjects(
  projects: readonly ProjectListItemDTO[],
  query: string,
): ProjectListItemDTO[] {
  const active = activeProjects(projects);
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return active;
  return active.filter((p) => projectSearchHaystack(p).includes(q));
}
